import styled, { css } from "styled-components";
import {
  Menu,
  Autocomplete,
  Dialog,
  Divider,
  CheckboxGroup,
  Chip,
  Input,
  StyledBaseInputField,
  Heading,
  HEADING_LEVELS,
  HEADING_TYPES,
  Content,
  InlineInput,
} from "@cdkglobal/radial";
import type { HeadingProps } from "@cdkglobal/radial";
import type React from "react";
import type { MenuPosition } from "../../types";

// RENDERERS STYLES
export const StyledSubTitle = styled(
  Heading as React.ComponentType<HeadingProps>,
).attrs({
  level: HEADING_LEVELS.LEVEL_3,
  type: HEADING_TYPES.SUBTITLE_1,
})`
  margin: 1px;
`;

export const StyledMenu = styled(Menu)`
  z-index: 3000;
`;

export const StyledInlineInput = styled(InlineInput)`
  label > svg[data-testid$="-dropdown-icon"] {
    order: 99;
  }
`;

export const StyledInputWithTooltip = styled(Input)`
  input {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  ${StyledBaseInputField} {
    cursor: pointer;
  }
  svg {
    cursor: pointer;
  }
`;

export const StyledAutocomplete = styled(Autocomplete)`
  z-index: 1000;
  margin-right: 8rem;

  && span[data-testid$="-primarytext"] {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.25rem;
    display: block;
  }
`;

export const StyledDialog = styled(Dialog)`
  max-width: 60vw;
  z-index: 2000;
`;

export const StyledCheckBoxGroup = styled(CheckboxGroup)`
  & > :nth-child(2) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;
    row-gap: 10px;
    margin: 0 30px;
  }
`;

export const StyledDivider = styled(Divider)`
  height: 1px;
  background-color: black;
`;

export const StyledThickDivider = styled(Divider)`
  height: 2px;
  background-color: black;
`;

export const InputWithButtonContainer = styled.div`
  padding: 5px 1rem;
  border: 1px solid gray;
  border-radius: 5px;
  margin-top: 0px;
  p {
    font-size: smaller;
    margin: 0;
    padding: 1px;
  }
`;
export const SelectedOptionContainer = styled.div`
  label {
    background-color: #fff;
  }
  svg {
    display: none;
  }
`;

export const PickerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-top: 5px;
  gap: 35px;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  padding-top: 16px;
  flex-direction: row;
  margin-left: auto;
  align-items: flex-end;
  gap: 10px;
`;

const positionStyles = css<{ $position?: MenuPosition }>`
  top: ${({ $position }) => ($position === "top" ? "auto" : "100%")};
  bottom: ${({ $position }) => ($position === "top" ? "100%" : "auto")};
  left: ${({ $position }) => ($position === "right" ? "auto" : "0")};
  right: ${({ $position }) => ($position === "right" ? "0" : "auto")};
`;

export const MegaPickerContainer = styled.div<{ $position?: MenuPosition }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem;
  gap: 0px;

  position: absolute;
  width: 100%;
  min-width: 342px;
  left: 0;
  right: 0;
  box-sizing: border-box;
  ${positionStyles}

  background: #ffffff;
  box-shadow: 0px 2px 7px 1px rgba(21, 27, 37, 0.45);
  border-radius: 8px;

  flex: none;
  order: 6;
  flex-grow: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdownMenu.value};
`;

export const InputAndPickerContainer = styled.div`
  position: relative;
`;

export const DatePickerPositionWrapper = styled.div<{
  $position?: MenuPosition;
}>`
  position: relative;

  && {
    .DayPicker {
      position: absolute;
    }

    .DayPicker {
      ${positionStyles}
    }
  }
`;

export const Title = styled(Heading as React.ComponentType<HeadingProps>).attrs(
  {
    level: HEADING_LEVELS.LEVEL_4,
    type: HEADING_TYPES.SUBTITLE_1,
  },
)`
  font-family: ${({ theme }) => theme.typography.subtitle3.fontFamily.value};
  font-style: normal;
  line-height: 0px;
  color: #151b25;
`;

export const StyledDialogCheckBox = styled(Dialog)`
  min-width: 50vw;
  min-height: 80vh;
  z-index: 2000;
`;

export const StyledExportRecordsDialog = styled(Dialog)`
  min-width: 30vw;
  min-height: 20vh;
  z-index: 2000;
`;

export const StyledARMigrationDialog = styled(Dialog)`
  min-width: 30vw;
  min-height: 20vh;
  z-index: 2000;
`;

export const StyledRecordDeletionDialog = styled(Dialog)`
  min-width: 30vw;
  min-height: 20vh;
  z-index: 2000;
`;

export const StyledFileUploadDialog = styled(Dialog)`
  max-width: 90vw;
  z-index: 2000;
`;

export const StyledTemplateUpload = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  flex-wrap: wrap;
`;

interface StyledLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  onClick?: () => void;
  "data-testid"?: string;
}

export const StyledLink = styled.label<StyledLabelProps>`
  padding-left: 5px;
  color: -webkit-link;
  cursor: pointer;
  text-decoration: underline;
`;

type StyledFileInputProps = {
  id: string;
  "data-testid": string;
  type: string;
  accept: string;
  multiple: boolean;
  ref: React.RefObject<HTMLInputElement>;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const StyledFileInput = styled.input<StyledFileInputProps>`
  display: none;
`;

export const StyledChip = styled(Chip)`
  margin-left: 0.5rem;
`;

export const StyledLabelWrapper = styled(Content)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
`;
